import React, { useEffect, useState, useCallback } from "react";

import PropTypes from "prop-types";
import { isEmpty, isNil } from "ramda";
import {
  Route,
  Routes,
  BrowserRouter,
  Navigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { setAxiosIntercepts } from "apis/axiosInstance/rails";
import { PageLoader, Toastr } from "clearsense-ui/components";
import {
  CLIENT_ROUTES,
  SETTINGS_ROUTES,
} from "clearsense-ui/constants/features";
import Login from "components/Authentication/Login";
import Container from "components/Common/Container";
import LandingPage from "components/Common/LandingPage";
import PricingPage from "components/Common/PricingPage";
import { useFeaturesDispatch } from "contexts/features";
import { SidebarProvider } from "contexts/sidebar";
import { useUserDispatch } from "contexts/user";
import { InitializeLogger } from "common/logger";
import { hasASubAppWithReadPermission } from "utils/roleBasedAccess";

import ErrorBoundary from "./Common/ErrorBoundary";
import Header from "./Common/Header";
import NoPermissionPage from "./Common/NoPermissionPage";
import PageNotFound from "./Common/PageNotFound";
import SidebarContainer from "./Common/SidebarContainer";

const Main = ({
  user,
  organization_logo_url,
  is_development_or_heroku_environment,
  domain,
  features,
}) => {
  const [loading, setLoading] = useState(true);
  const userDispatch = useUserDispatch();
  const featuresDispatch = useFeaturesDispatch();
  const setAxiosInterceptsCallback = useCallback(() => {setAxiosIntercepts()},[]);

  useEffect(() => {
    setAxiosInterceptsCallback()
    InitializeLogger();
    if (user && !isEmpty(user)) {
      user.organization_logo_url = organization_logo_url;
      userDispatch({
        type: "SET_USER",
        payload: { user },
      });
      setLoading(false);
    }
    featuresDispatch({ type: "SET_FEATURES", payload: { features, user } });
    setLoading(false);
    // eslint-disable-next-line
  }, [user, userDispatch]);

  if (loading) {
    return (
      <div className="h-screen">
        <PageLoader />
      </div>
    );
  }

  const RequireAuth = ({ children }) => {
    const location = useLocation();
    if (isEmpty(user) || isNil(user)) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children ? children : <Outlet />;
  };

  const DashBoardProviders = ({ children }) => (
    <SidebarProvider>
      <Header features={features} domain={domain} />
      <SidebarContainer features={features}>
        <Container>
          <RequireAuth>{children}</RequireAuth>
        </Container>
      </SidebarContainer>
    </SidebarProvider>
  );

  return (
    <BrowserRouter>
      <ToastContainer />
      <ErrorBoundary>
        <Routes>
          <Route exact path="/" element={<LandingPage user={user} />} />
          <Route path="/login" element={<Login user={user}/>} />
          {features?.header?.dropdown?.includes("Pricing") && (
            <Route path="/pricing" element={<PricingPage user={user} />} />
          )}
          <Route element={<DashBoardProviders />}>
            {Object.keys(CLIENT_ROUTES).map(client_route => {
              if (
                hasASubAppWithReadPermission(
                  features?.client_apps?.[client_route],
                  user
                )
              ) {
                const Component = CLIENT_ROUTES?.[client_route]?.component;
                return (
                  <Route
                    key={CLIENT_ROUTES[client_route]?.path}
                    path={CLIENT_ROUTES[client_route]?.path}
                    element={
                      <Component
                        features={features?.client_apps?.[client_route]}
                      />
                    }
                  />
                );
              }

              return (
                <Route
                  key={CLIENT_ROUTES[client_route]?.path}
                  path={CLIENT_ROUTES[client_route]?.path}
                  element={<NoPermissionPage />}
                />
              );
            })}
            {Object.keys(SETTINGS_ROUTES).map(route => {
              const Component = SETTINGS_ROUTES?.[route]?.component;
              if (features?.header?.[route]) {
                return (
                  <Route
                    key={SETTINGS_ROUTES[route]?.path}
                    path={SETTINGS_ROUTES[route]?.path}
                    element={<Component features={features?.header?.[route]} />}
                  />
                );
              }

              return "";
            })}
            {features?.header?.links?.map(feature => {
              if (CLIENT_ROUTES[feature]?.path) {
                const Component = CLIENT_ROUTES[feature]?.component;
                return (
                  <Route
                    key={CLIENT_ROUTES[feature]?.path}
                    path={CLIENT_ROUTES[feature]?.path}
                    element={<Component />}
                  />
                );
              }

              return "";
            })}
            {features?.sidebar_footer?.links?.map(feature =>
              CLIENT_ROUTES[feature]?.map(subRoutes => {
                const Component = subRoutes?.component;
                return (
                  <Route
                    key={subRoutes?.path}
                    path={subRoutes?.path}
                    element={<Component />}
                  />
                );
              })
            )}
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

Main.propTypes = {
  user: PropTypes.object,
  flash: PropTypes.string,
};

export default Main;
